import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { collection, getDocs, doc, getDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import FriendRequests from "./FriendRequests";
import FriendSearch from "./FriendSearch";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

const FriendsPage = () => {
    const { currentUser } = useAuth();
    const [friends, setFriends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [friendToRemove, setFriendToRemove] = useState(null); // Для модального окна
    const [removingFriendId, setRemovingFriendId] = useState(null);
    const location = useLocation();

    const fetchFriends = async () => {
        if (!currentUser) return;

        try {
            const friendsCol = collection(db, "users", currentUser.uid, "friends");
            const friendsSnapshot = await getDocs(friendsCol);

            const friendsData = await Promise.all(
                friendsSnapshot.docs.map(async (friendDoc) => {
                    const friendId = friendDoc.id;
                    const userDoc = await getDoc(doc(db, "users", friendId));
                    if (userDoc.exists()) {
                        return {
                            id: friendId,
                            ...userDoc.data(),
                            avatarUrl: userDoc.data().avatarUrl || userDoc.data().photoURL || null
                        };
                    }
                    return null;
                })
            );

            setFriends(friendsData.filter(Boolean));
        } catch (error) {
            console.error("Ошибка загрузки друзей:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFriends(); // 💡 просто вызывай функцию
    }, [currentUser, location]);

    const handleConfirmRemove = () => {
        if (!friendToRemove || !currentUser) return;

        const friend = friendToRemove;

        setFriendToRemove(null); // Мгновенно закрываем модалку

        // Делаем небольшую паузу, чтобы успела скрыться модалка (например, 100 мс)
        setTimeout(() => {
            setRemovingFriendId(friend.id); // Запускаем глитч-анимацию

            // Теперь подождём 2 секунды, пока идёт глюк, а потом удалим
            setTimeout(async () => {
                try {
                    await deleteDoc(doc(db, "users", currentUser.uid, "friends", friend.id));
                    await deleteDoc(doc(db, "users", friend.id, "friends", currentUser.uid));

                    setFriends((prev) => prev.filter(f => f.id !== friend.id));
                    setRemovingFriendId(null);
                } catch (error) {
                    console.error("Ошибка при удалении друга:", error);
                }
            }, 2000);
        }, 100); // Подождать 100 мс для скрытия модалки
    };



    if (!currentUser) return <p style={{ color: "#ff6b6b", textAlign: "center" }}>Пожалуйста, войдите в систему.</p>;
    if (loading) return <p style={{ color: "#aaa", textAlign: "center" }}>Загрузка друзей...</p>;

    return (
        <div style={{ maxWidth: "1000px", margin: "0 auto" }} className="background-personalWall">

            <FriendSearch />
            <FriendRequests />

            <h1 className="wall-header">Мои друзья</h1>

            {friends.length === 0 ? (
                <p style={{ color: "#aaa" }}>У вас пока нет друзей.</p>
            ) : (
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                    gap: "15px",
                    marginTop: "15px"
                }}>
                    <AnimatePresence>
                        {friends.map((friend) => {
                            const isRemoving = removingFriendId === friend.id;

                            return (
                                <motion.div
                                    key={friend.id}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{
                                        opacity: isRemoving ? [1, 0.8, 0.5, 0] : 1,
                                        scale: isRemoving ? [1, 1.2, 0.8, 0] : 1,
                                        rotate: isRemoving ? [0, 5, -5, 15] : 0
                                    }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{ duration: isRemoving ? 2 : 0.3 }}
                                    style={{
                                        background: "#2b2d42",
                                        borderRadius: "10px",
                                        padding: "12px",
                                        textAlign: "center",
                                        position: "relative",
                                        overflow: "hidden",
                                        filter: isRemoving ? "contrast(2) saturate(2)" : "none",
                                        clipPath: isRemoving ? "polygon(0% 0%, 100% 10%, 90% 100%, 10% 90%)" : "none",
                                        transform: isRemoving ? "skewX(-3deg)" : "none"
                                    }}
                                >
                                    {/* Аватар */}
                                    <Link to={`/wall/${friend.id}`} style={{
                                        display: "block",
                                        width: "60px",
                                        height: "60px",
                                        margin: "0 auto 8px",
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        background: "#3a3b5a"
                                    }}>
                                        {friend.avatarUrl ? (
                                            <img
                                                src={friend.avatarUrl}
                                                alt="Аватар"
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        ) : (
                                            <span style={{
                                                display: "flex",
                                                width: "100%",
                                                height: "100%",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "white",
                                                fontSize: "20px"
                                            }}>
                                                {friend.displayName?.charAt(0) || "?"}
                                            </span>
                                        )}
                                    </Link>

                                    {/* Имя */}
                                    <Link to={`/wall/${friend.id}`} style={{
                                        color: "white",
                                        textDecoration: "none",
                                        display: "block",
                                        marginBottom: "8px",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        fontSize: "0.8rem"
                                    }}>
                                        {friend.displayName || "Аноним"}
                                    </Link>

                                    {/* Кнопка написать */}
                                    <button style={{
                                        background: "#0095ffff",
                                        border: "none",
                                        color: "white",
                                        padding: "1px 4px",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "10px",
                                        display: "block",
                                        position: "relative",
                                        top: "-6px",
                                        margin: "0 auto 1px",
                                        whiteSpace: "nowrap",
                                        transition: "background 0.2s"
                                    }}>
                                        Написать
                                    </button>

                                    {/* Кнопка удалить */}
                                    <button
                                        onClick={() => setFriendToRemove(friend)}
                                        style={{
                                            background: "#ff6b6b",
                                            border: "none",
                                            color: "white",
                                            padding: "3px 4px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "8px",
                                            display: "block",
                                            position: "relative",
                                            top: "4px",
                                            margin: "0 auto 1px",
                                            whiteSpace: "nowrap",
                                            transition: "background 0.2s"
                                        }}
                                    >
                                        Удалить из друзей
                                    </button>
                                </motion.div>
                            );
                        })}

                    </AnimatePresence>
                </div>
            )}

            {/* Модалка подтверждения */}
            {friendToRemove && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999
                }}>
                    <div style={{
                        background: "#1e1e2e",
                        padding: "20px 30px",
                        borderRadius: "12px",
                        textAlign: "center",
                        color: "white",
                        maxWidth: "300px"
                    }}>
                        <p style={{ marginBottom: "15px" }}>
                            Удалить <strong>{friendToRemove.displayName || "друга"}</strong> из друзей?
                        </p>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <button
                                onClick={() => setFriendToRemove(null)}
                                style={{
                                    background: "#ccc",
                                    border: "none",
                                    padding: "5px 10px",
                                    borderRadius: "5px",
                                    cursor: "pointer"
                                }}
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleConfirmRemove}
                                style={{
                                    background: "#ff6b6b",
                                    border: "none",
                                    padding: "5px 10px",
                                    borderRadius: "5px",
                                    color: "white",
                                    cursor: "pointer"
                                }}
                            >
                                Удалить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FriendsPage;